/**
 * AI Upgrade Modal
 * 
 * Shown when user reaches their AI conversation limit
 * Encourages upgrade to Premium with clear benefits
 */

import React from 'react';
import { X } from '../icons';
import { AssistantIcon, QuickIcon, GoalIcon, GrowthIcon, ICON_STYLES } from '../icons';

interface AIUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  charactersUsed: number;
  limit: number;
  resetDate: Date;
  onUpgrade?: () => void;
}

const AIUpgradeModal: React.FC<AIUpgradeModalProps> = ({
  isOpen,
  onClose,
  charactersUsed,
  limit,
  resetDate,
  onUpgrade
}) => {
  if (!isOpen) return null;

  const formatResetDate = (date: Date) => {
    return new Intl.DateTimeFormat('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  };

  const percentage = Math.min(100, (charactersUsed / limit) * 100);

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      // Navigate to pricing/subscription page
      window.location.hash = 'dashboard';
      // TODO: Open subscription modal when implemented
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="relative bg-orange-600 p-6 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            aria-label="Sluiten"
          >
            <X size={20} />
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <AssistantIcon size={24} weight="regular" />
            </div>
            <div>
              <h2 className="text-2xl font-medium">Gesprekslimiet Bereikt</h2>
              <p className="text-white/90 text-sm">Upgrade voor onbeperkte gesprekken</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Usage Stats */}
          <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Characters gebruikt</span>
              <span className="text-2xl font-medium text-orange-600">
                {formatNumber(charactersUsed)} / {formatNumber(limit)}
              </span>
            </div>
            <div className="w-full bg-white rounded-full h-2 overflow-hidden">
              <div
                className="h-2 bg-orange-600 transition-all duration-500"
                style={{ width: `${percentage}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Reset op {formatResetDate(resetDate)}
            </p>
          </div>

          {/* Premium Benefits */}
          <div>
            <h3 className="text-lg font-medium text-gray-700 mb-3">
              Met Premium krijg je
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <QuickIcon size={16} weight="regular" className="text-orange-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-700">Onbeperkte gesprekken</p>
                  <p className="text-sm text-gray-500">Geen limieten op je interacties</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <GoalIcon size={16} weight="regular" className="text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-700">Automatische inzichten</p>
                  <p className="text-sm text-gray-500">VibeScore analyse & gepersonaliseerde coaching</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <GrowthIcon size={16} weight="regular" className="text-gray-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-700">Plus veel meer!</p>
                  <p className="text-sm text-gray-500">Onbeperkte posts, video uploads, API integraties</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col gap-3 pt-4">
            <button
              onClick={handleUpgrade}
              className="w-full py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 hover:shadow-lg transition-all"
            >
              Upgrade naar Premium
            </button>
            <button
              onClick={onClose}
              className="w-full py-3 bg-gray-50 text-gray-600 font-medium rounded-lg hover:bg-gray-100 transition-colors"
            >
              Later
            </button>
          </div>

          {/* Footer note */}
          <p className="text-xs text-center text-gray-500">
            Je gratis characters worden op {formatResetDate(resetDate)} gereset
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIUpgradeModal;

